import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { faCar, faPersonWalking } from '@fortawesome/free-solid-svg-icons';
import { useFormikContext } from 'formik';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FaIcon } from '../common/FaIcon';
import { SpzAutocomplete } from '../common/form/SpzAutocomplete';
import { VisitationFormContext } from './VisitationAddModalPin';

interface VisitationModeForm {
  mode: 'walk' | 'car';
  spz?: string;
}

export const VisitationModeSelector: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<VisitationModeForm>();
  const visitationContext = useContext(VisitationFormContext);

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'walk' | 'car' | null) => {
    if (newMode == null) return;
    setFieldValue('mode', newMode);
    // Reset touched state for carRegistration when switching modes
    if (newMode === 'car') setFieldTouched('spz', false);
    if (newMode === 'walk') {
      setFieldValue('spz', '');
      visitationContext?.setHasValidCar(false);
    }
  };

  return (
    <>
      {/*Mode toggle (walk/car) */}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12, mb: 0.5, display: 'block' }}
        >
          {t('visitation.eventType')}
        </Typography>
        <ToggleButtonGroup
          value={values.mode}
          exclusive
          onChange={handleModeChange}
          fullWidth
          sx={{
            backgroundColor: '#eee',
            padding: '4px',
            borderRadius: '8px',
            '& .MuiToggleButton-root': {
              flex: 1,
              border: 'none',
              borderRadius: '6px !important',
              fontWeight: 600,
              fontSize: 15,
              color: '#666',
              textTransform: 'none',
              padding: '10px',
              '&.Mui-selected': {
                backgroundColor: 'white',
                color: 'var(--primary-color, #ff6b35)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: 'white',
                },
              },
            },
          }}
        >
          <ToggleButton value="walk">
            <FaIcon icon={faPersonWalking} style={{ marginRight: '8px' }} />
            {t('visitation.walkEntry')}
          </ToggleButton>
          <ToggleButton value="car">
            <FaIcon icon={faCar} style={{ marginRight: '8px' }} />
            {t('visitation.carEntry')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Car registration (conditional) */}
      {values.mode === 'car' && (
        <Box
          sx={{
            mt: 2,
            animation: 'slideIn 0.3s ease',
            '@keyframes slideIn': {
              from: { opacity: 0, transform: 'translateY(-10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12, mb: 1, display: 'block' }}
          >
            {t('visitation.carRegistrationStep')}
          </Typography>
          <SpzAutocomplete name="spz" label={t('supplier.car.spz')} required fullWidth />
        </Box>
      )}
    </>
  );
};
